import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { STATUS_LABEL, SENDER_LABEL } from './speakupConstants';
import SpeakUpShell from './SpeakUpShell';
import './speakup.css';

export default function SpeakUpThread() {
  const { token } = useParams();
  const [messages, setMessages] = useState(null);
  const [status, setStatus] = useState(null);
  const [lane, setLane] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  async function load() {
    const { data, error: rpcError } = await supabase.rpc('get_thread', { p_token: token });
    if (rpcError) { setError('not_found'); return; }
    setMessages(data || []);
    if (data && data[0]) setStatus(data[0].status);
    if (data && data[0]) setLane(data[0].lane);
  }

  useEffect(() => { load(); }, [token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function handleReply(e) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    const { error: rpcError } = await supabase.rpc('reply_to_thread', { p_token: token, p_body: reply.trim() });
    setSending(false);
    if (rpcError) { setError('reply_error'); return; }
    setReply('');
    load();
  }

  if (error === 'not_found') {
    return (
      <SpeakUpShell>
        <div className="speakup-error">This link isn't valid, or the conversation has been removed.</div>
      </SpeakUpShell>
    );
  }

  return (
    <SpeakUpShell>
        <h1 className="speakup-h1">Your conversation</h1>
        {status && <span className="speakup-chip speakup-chip-status">{STATUS_LABEL[status] || status}</span>}
        <p className="speakup-note" style={{ marginTop: 10 }}>
          {lane === 'idea'
            ? 'Your branch manager and ownership can see this. Neither of them knows who you are.'
            : 'This goes to ownership, and to nobody else. They still don’t know who you are.'}
        </p>

        {/* #478's fourth ruling, OOB-6. Every answer used to render through
            one class with no label, so a branch manager's reply and
            ownership's reply were indistinguishable to the person who
            submitted. The database knew which was which the whole time.

            React.Fragment rather than a wrapping div: .speakup-thread is a
            flex column and the label is a sibling of the bubble, not a parent
            of it, so the existing alignment is untouched. */}
        <div className="speakup-thread">
          {messages && messages.map((m, i) => {
            const mine = m.sender === 'submitter';
            const from = mine ? null : SENDER_LABEL[m.sender];
            return (
              <React.Fragment key={i}>
                {from && <div className="speakup-msg-from">{from}</div>}
                <div className={mine ? 'speakup-msg speakup-msg-them' : 'speakup-msg speakup-msg-company'}>
                  {m.body}
                </div>
              </React.Fragment>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {error === 'reply_error' && <div className="speakup-error">Could not send that. Try again.</div>}

        <form onSubmit={handleReply} className="speakup-reply-row">
          <input
            className="speakup-input"
            placeholder="Reply anonymously…"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          <button className="speakup-btn-sm speakup-btn-dark" type="submit" disabled={sending}>
            {sending ? '…' : 'Send'}
          </button>
        </form>
        <p className="speakup-note" style={{ marginTop: 10 }}>
          Bookmark this page to check back. No inbox, no login — nothing here identifies you.
        </p>
    </SpeakUpShell>
  );
}